import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Search, Lightbulb, Code, Rocket, CheckCircle, Users, Clock, Award, Target } from 'lucide-react';

const HowItWorks: React.FC = () => {
  const process = [
    {
      step: '01',
      title: 'Discovery & Strategy',
      description: 'We begin by understanding your brand, goals, and target audience to create a comprehensive digital strategy.',
      icon: Search,
      details: [
        'Brand Analysis & Positioning',
        'Target Audience Research',
        'Competitive Analysis',
        'Goal Setting & KPIs',
        'Strategy Development',
        'Timeline Planning'
      ],
      duration: '1-2 weeks',
      deliverables: ['Brand Analysis Report', 'Digital Strategy Document', 'Project Timeline']
    },
    {
      step: '02',
      title: 'Design & Planning',
      description: 'Our creative team designs stunning visuals and user experiences that reflect your luxury brand identity.',
      icon: Lightbulb,
      details: [
        'UI/UX Design',
        'Brand Guidelines',
        'Wireframing & Prototyping',
        'Visual Design System',
        'User Journey Mapping',
        'Design Review & Approval'
      ],
      duration: '2-3 weeks',
      deliverables: ['Design Mockups', 'Brand Guidelines', 'Interactive Prototypes']
    },
    {
      step: '03',
      title: 'Development & Build',
      description: 'We bring your vision to life using cutting-edge technology and best practices for optimal performance.',
      icon: Code,
      details: [
        'Frontend Development',
        'Backend Development',
        'Database Design',
        'API Integration',
        'Quality Assurance Testing',
        'Performance Optimization'
      ],
      duration: '4-8 weeks',
      deliverables: ['Fully Functional Website/App', 'Testing Reports', 'Performance Metrics']
    },
    {
      step: '04',
      title: 'Launch & Optimize',
      description: 'We launch your project and continuously monitor and optimize for maximum performance and results.',
      icon: Rocket,
      details: [
        'Deployment & Go-Live',
        'Performance Monitoring',
        'Analytics Setup',
        'SEO Optimization',
        'User Training',
        'Ongoing Support'
      ],
      duration: '1-2 weeks',
      deliverables: ['Live Project', 'Analytics Dashboard', 'Support Documentation']
    }
  ];

  const methodologies = [
    {
      icon: Users,
      title: 'Collaborative Approach',
      description: 'We work closely with your team throughout the entire process, ensuring alignment and transparency.'
    },
    {
      icon: Clock,
      title: 'Agile Methodology',
      description: 'Flexible approach that adapts to your needs while maintaining quality and timeline commitments.'
    },
    {
      icon: Award,
      title: 'Quality Assurance',
      description: 'Rigorous testing and quality checks at every stage to ensure exceptional results.'
    },
    {
      icon: Target,
      title: 'Results-Driven',
      description: 'Every step is designed to deliver measurable results that align with your business objectives.'
    }
  ];

  const tools = [
    { name: 'Figma', category: 'Design' },
    { name: 'Adobe Creative Suite', category: 'Design' },
    { name: 'React/Next.js', category: 'Development' },
    { name: 'React Native', category: 'Development' },
    { name: 'Expo', category: 'Development' },
    { name: 'WordPress', category: 'CMS' },
    { name: 'Shopify', category: 'E-commerce' },
    { name: 'Google Analytics', category: 'Analytics' },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-bold mb-6">
              How We <span className="text-gradient">Work</span>
            </h1>
            <p className="text-xl text-neutral-600 leading-relaxed">
              Our proven process ensures exceptional results for brands. We combine strategic thinking with creative execution to deliver digital experiences that exceed expectations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Our Process</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              We follow a structured approach that ensures transparency, quality, and results at every stage of your project.
            </p>
          </motion.div>

          <div className="space-y-16">
            {process.map((phase, index) => (
              <motion.div
                key={phase.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                      {phase.step}
                    </div>
                    <div>
                      <h3 className="text-2xl font-serif font-semibold">{phase.title}</h3>
                      <div className="text-sm text-neutral-500">Duration: {phase.duration}</div>
                    </div>
                  </div>

                  <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                    {phase.description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div>
                      <h4 className="font-semibold text-neutral-900 mb-3">What We Do:</h4>
                      <ul className="space-y-2">
                        {phase.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-primary-500 flex-shrink-0" />
                            <span className="text-sm text-neutral-700">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-neutral-900 mb-3">Deliverables:</h4>
                      <ul className="space-y-2">
                        {phase.deliverables.map((deliverable, deliverableIndex) => (
                          <li key={deliverableIndex} className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-accent-500 flex-shrink-0" />
                            <span className="text-sm text-neutral-700">{deliverable}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Visual */}
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="bg-gradient-to-br from-primary-100 to-accent-100 rounded-2xl p-8 h-80 flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <phase.icon className="w-10 h-10 text-white" />
                      </div>
                      <h4 className="text-xl font-semibold mb-2">{phase.title}</h4>
                      <p className="text-neutral-600 text-sm">Step {phase.step}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodologies */}
      <section className="py-24 bg-neutral-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Our Methodologies</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              We combine proven methodologies with luxury brand expertise to deliver exceptional results.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {methodologies.map((methodology, index) => (
              <motion.div
                key={methodology.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <methodology.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-4">{methodology.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{methodology.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Tools & Technologies</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              We use industry-leading tools and cutting-edge technologies to deliver premium results.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {tools.map((tool, index) => (
              <motion.div
                key={tool.name}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: false }}
                className="bg-neutral-50 rounded-lg p-6 text-center hover:bg-neutral-100 transition-colors duration-300"
              >
                <div className="text-lg font-semibold text-neutral-900 mb-2">{tool.name}</div>
                <div className="text-sm text-neutral-500">{tool.category}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-neutral-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold mb-6">Typical Project Timeline</h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              While every project is unique, here's a general timeline for our most common projects.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                { project: 'Website Development', duration: '8-12 weeks', phases: ['Strategy (1-2 weeks)', 'Design (2-3 weeks)', 'Development (4-6 weeks)', 'Launch (1 week)'] },
                { project: 'Mobile App Development', duration: '12-20 weeks', phases: ['Strategy (2-3 weeks)', 'Design (3-4 weeks)', 'Development (6-12 weeks)', 'Launch (1-2 weeks)'] },
                { project: 'Digital Marketing Campaign', duration: '4-8 weeks', phases: ['Strategy (1-2 weeks)', 'Setup (1-2 weeks)', 'Launch (2-4 weeks)', 'Optimization (Ongoing)'] }
              ].map((item, index) => (
                <motion.div
                  key={item.project}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: false }}
                  className="bg-white rounded-xl p-6 shadow-sm"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                    <h3 className="text-xl font-semibold text-neutral-900">{item.project}</h3>
                    <div className="text-primary-600 font-semibold">{item.duration}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {item.phases.map((phase, phaseIndex) => (
                      <span key={phaseIndex} className="bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-sm">
                        {phase}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-primary-500 to-accent-500">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
          >
            <h2 className="text-4xl font-serif font-bold text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's discuss your project and how our proven process can help you achieve exceptional results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-accent-50 transition-colors duration-300 inline-flex items-center justify-center">
                Start Your Project
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/services" className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/30 transition-colors duration-300 inline-flex items-center justify-center">
                View Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
